"use client";

import { RouteConfig } from "@/configs/types/route";
import { useI18n } from "@/i18n";
import { PageTitle } from "@repo/uikit";
import Image from "next/image";
import Link from "next/link";
import CodeStep from "./code-step";
import EmailStep from "./email-step";
import { type UseLoginOptions, useLogin } from "./useLogin";

export interface LoginFormProps extends UseLoginOptions {
  showLogo?: boolean;
  className?: string;
}

export function LoginForm({ onSuccess, showLogo = true, className }: LoginFormProps) {
  const { t } = useI18n();
  const {
    step,
    email,
    setEmail,
    codeValue,
    isSending,
    isVerifying,
    hasVerificationError,
    canSubmitEmail,
    emailError,
    codeError,
    canResend,
    resendSeconds,
    turnstileSiteKey,
    turnstileResetKey,
    setTurnstileToken,
    requestLogin,
    verifyCode,
    handleCodeChange,
    goBackToEmail,
  } = useLogin({
    onSuccess,
  });

  return (
    <div className={`grid grid-cols-1 gap-4 ${className || ""}`} data-testid="login-form">
      <div className="flex flex-col items-center gap-3 text-center">
        {showLogo && (
          <Link href={RouteConfig.Home} className="shrink-0 hover:cursor-pointer" aria-label="Cantabook Home" data-testid="login-form-logo">
            <Image src="/canta-120.png" alt="Cantabook Logo" width={48} height={48} className="w-12 h-auto dark:invert" priority />
          </Link>
        )}
        <PageTitle size="sm" data-testid="login-title" data-i18n="auth.signInTitle">
          {t("auth.signInTitle")}
        </PageTitle>
      </div>

      {step === "email" && (
        <EmailStep
          email={email}
          setEmail={setEmail}
          isSending={isSending}
          canSubmitEmail={canSubmitEmail}
          emailError={emailError}
          turnstileSiteKey={turnstileSiteKey}
          turnstileResetKey={turnstileResetKey}
          onTurnstileToken={setTurnstileToken}
          onSubmit={requestLogin}
        />
      )}

      {step === "code" && (
        <CodeStep
          codeValue={codeValue}
          isVerifying={isVerifying}
          hasError={hasVerificationError}
          codeError={codeError}
          onCodeChange={handleCodeChange}
          onVerify={() => verifyCode()}
          onGoBack={goBackToEmail}
          isSending={isSending}
          canResend={canResend}
          resendSeconds={resendSeconds}
          onResend={requestLogin}
        />
      )}
    </div>
  );
}

export default LoginForm;
